import {
  APPROVAL_MESSAGES,
  APPROVAL_SIMULATION_NOTE,
  approvalCardPath,
  isApprovalInFlight,
} from '@fos/shared/contracts/approvals';
import type { OfferDetail, OfferVersionDto } from '@fos/shared/contracts/offers';
import { OFFER_STATUS_LABELS, OFFER_STATUS_TONES } from '@fos/shared/labels';
import {
  CircleCheck,
  CircleX,
  CopyPlus,
  FileText,
  MoreHorizontal,
  Pencil,
  Send,
  ShieldCheck,
  Undo2,
  UserCheck,
} from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { appErrorCode, errorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { formatDate, formatDateShort, formatDuration } from '../../../lib/format';
import { useRememberRecent } from '../../../lib/recent';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { ContextBanner } from '../../../ui/Blocks';
import { Button, IconButton } from '../../../ui/Button';
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from '../../../ui/Menu';
import { Mono } from '../../../ui/Mono';
import { Page } from '../../../ui/Page';
import { Breadcrumbs } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, Skeleton } from '../../../ui/States';
import { StatusPill } from '../../../ui/StatusPill';
import { toast } from '../../../ui/Toast';
import { TOUCH_BUTTON } from '../../mobile';
import { ModulePage, NotFoundPage } from '../../modules/pages/ModulePage';
import { OfferContentView } from '../components/OfferContentView';
import { isSendWaiting, OfferEditor } from '../components/OfferEditor';
import { type OfferOutcome, OutcomeDialog } from '../components/OutcomeDialog';
import { SendOfferDialog } from '../components/SendOfferDialog';
import { VersionCompare } from '../components/VersionCompare';
import { offerMoneyText, offerNumberText, offerRateText, versionText } from '../offer-format';

/**
 * Oferta (/oferty/:id; ekran czeka na projekt w Figmie — wzorzec „Zestawienie” 09 i „Notatka” 10): okruszki,
 * tytuł ze statusem, linia meta, akcje (Edytuj, Nowa wersja, więcej: przyjęta / odrzucona / cofnij do roboczej),
 * banery (wysyłka w Akceptacjach, oferta u właściciela), treść wersji sekcjami albo edycja, po prawej
 * „Podsumowanie” (czas, stawka, razem netto mono, jedna akcja główna: „Wyślij do klienta” → Akceptacje albo
 * „Przekaż właścicielowi”) i lista wersji z porównaniem z poprzednią.
 */
export function OfferDetailPage() {
  const access = useAccess();
  const { id } = useParams();
  if (!access.can('clients')) return <ModulePage module="clients" />;
  if (!id) return <NotFoundPage />;
  return <OfferDetailView id={id} />;
}

/** Odświeżanie oferty, gdy wysyłka jest zatwierdzona i czeka na wykonanie albo się wykonuje. */
const SEND_STATE_POLL_MS = 2000;

function OfferDetailView({ id }: { id: string }) {
  const query = trpc.offers.get.useQuery(
    { id },
    {
      refetchInterval: (q) =>
        isApprovalInFlight((q.state.data as OfferDetail | undefined)?.sendApproval) ? SEND_STATE_POLL_MS : false,
    },
  );
  if (query.isPending) {
    return (
      <Page>
        <div role="status" aria-label="Wczytywanie oferty">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="mt-4 h-7 w-72" />
          <Skeleton className="mt-2 h-4 w-96 max-w-full" />
          <div className="mt-8 grid gap-4 md:gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
            <Skeleton className="h-64 rounded-xl" />
            <Skeleton className="h-48 rounded-xl" />
          </div>
        </div>
      </Page>
    );
  }
  if (query.isError) {
    const code = appErrorCode(query.error);
    return (
      <Page>
        <Breadcrumbs items={[{ label: 'Oferty', to: '/oferty' }]} />
        <Section className="mt-6">
          {code === 'NOT_FOUND' || code === 'VALIDATION_ERROR' ? (
            <EmptyState
              icon={FileText}
              title="Nie ma takiej oferty"
              description="Oferta mogła zostać usunięta albo nie masz do niej dostępu."
            />
          ) : (
            <ErrorState onRetry={() => void query.refetch()} retrying={query.isFetching} />
          )}
        </Section>
      </Page>
    );
  }
  return <OfferDetailContent offer={query.data} />;
}

function OfferDetailContent({ offer }: { offer: OfferDetail }) {
  const access = useAccess();
  useRememberRecent({
    type: 'offer',
    id: offer.id,
    title: offer.title,
    subtitle: `oferta #${offer.number} · ${offer.client.name}`,
    to: `/oferty/${offer.id}`,
  });
  const seesRates = access.flag('seesRates');
  const utils = trpc.useUtils();
  const [params, setParams] = useSearchParams();
  const editing = params.get('edytuj') === '1' && offer.permissions.edit;
  const setEditing = (on: boolean) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (on) p.set('edytuj', '1');
        else p.delete('edytuj');
        return p;
      },
      { replace: true },
    );

  const [selectedNo, setSelectedNo] = useState(offer.currentVersion);
  const [compare, setCompare] = useState(false);
  const [dialog, setDialog] = useState<'send' | OfferOutcome | null>(null);
  // nowa wersja albo odświeżenie: pokaż bieżącą
  useEffect(() => {
    setSelectedNo(offer.currentVersion);
    setCompare(false);
  }, [offer.currentVersion]);

  const newVersion = trpc.offers.newVersion.useMutation();
  const submit = trpc.offers.submitToOwner.useMutation();
  const back = trpc.offers.backToDraft.useMutation();

  const current = offer.versions.find((v) => v.versionNo === offer.currentVersion);
  const selected = offer.versions.find((v) => v.versionNo === selectedNo) ?? current;
  const previous = selected ? offer.versions.find((v) => v.versionNo === selected.versionNo - 1) : undefined;
  const viewingOld = selected !== undefined && selected.versionNo !== offer.currentVersion;
  const p = offer.permissions;

  const run = async (action: () => Promise<unknown>, done: string) => {
    try {
      await action();
      toast.success(done);
      await utils.offers.invalidate();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const onNewVersion = () =>
    run(
      async () => {
        await newVersion.mutateAsync({ id: offer.id, version: offer.version });
        setEditing(true);
      },
      `Utworzono wersję ${offer.currentVersion + 1}`,
    );

  const meta = [
    offerNumberText(offer.number),
    offer.client.name,
    `wersja ${offer.currentVersion}${offer.versions.length > 1 ? ` z ${offer.versions.length}` : ''}`,
    offer.createdBy ? `autor ${offer.createdBy.name}` : null,
    offer.acceptedAt && offer.status === 'accepted' ? `przyjęta ${formatDate(offer.acceptedAt)}` : null,
    offer.rejectedAt && offer.status === 'rejected' ? `odrzucona ${formatDate(offer.rejectedAt)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const moreItems: ReactNode[] = [];
  if (p.markOutcome && offer.status !== 'accepted') {
    moreItems.push(
      <MenuItem key="acc" icon={CircleCheck} onSelect={() => setDialog('accepted')}>
        Oznacz jako przyjętą
      </MenuItem>,
    );
  }
  if (p.markOutcome && offer.status !== 'rejected') {
    moreItems.push(
      <MenuItem key="rej" icon={CircleX} onSelect={() => setDialog('rejected')}>
        Oznacz jako odrzuconą
      </MenuItem>,
    );
  }
  if (p.backToDraft) {
    if (moreItems.length > 0) moreItems.push(<MenuSeparator key="sep" />);
    moreItems.push(
      <MenuItem
        key="back"
        icon={Undo2}
        onSelect={() =>
          void run(
            () => back.mutateAsync({ id: offer.id, version: offer.version }),
            isSendWaiting(offer)
              ? 'Oferta wróciła do roboczej. Prośba o wysyłkę została wycofana.'
              : 'Oferta wróciła do roboczej',
          )
        }
      >
        Cofnij do roboczej
      </MenuItem>,
    );
  }

  return (
    <Page>
      <Breadcrumbs
        items={[
          { label: 'Oferty', to: '/oferty' },
          { label: offer.client.name, to: `/klienci/${offer.clientId}?zakladka=oferty` },
        ]}
      />
      <header className="mt-2.5 flex flex-col gap-4 md:flex-row md:items-center">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
            <h1 className="min-w-0 text-h3 break-words text-fg">{offer.title}</h1>
            <StatusPill tone={OFFER_STATUS_TONES[offer.status]}>{OFFER_STATUS_LABELS[offer.status]}</StatusPill>
          </div>
          <p className="text-body-s text-fg-secondary">{meta}</p>
        </div>
        {!editing && (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {p.edit && !viewingOld && (
              <Button variant="secondary" icon={Pencil} onClick={() => setEditing(true)}>
                Edytuj
              </Button>
            )}
            {p.newVersion && (
              <Button variant="secondary" icon={CopyPlus} loading={newVersion.isPending} onClick={onNewVersion}>
                Nowa wersja
              </Button>
            )}
            {moreItems.length > 0 && (
              <Menu>
                <MenuTrigger asChild>
                  <IconButton icon={MoreHorizontal} label="Więcej akcji" size={32} iconSize={18} />
                </MenuTrigger>
                <MenuContent align="end">{moreItems}</MenuContent>
              </Menu>
            )}
          </div>
        )}
      </header>

      <Banners offer={offer} />

      <div className="mt-6 grid gap-4 md:gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        {/* treść oferty w białej sekcji; podsumowanie i wersje obok jako karty */}
        <Section className="order-2 empty:hidden lg:order-1">
          {editing ? (
            <OfferEditor offer={offer} onDone={() => setEditing(false)} />
          ) : selected ? (
            <div className="flex flex-col gap-5">
              {viewingOld && (
                <ContextBanner tone="info" icon={FileText}>
                  Oglądasz {versionText(selected).toLocaleLowerCase('pl-PL')}
                  {selected.sentTo?.length ? ` do ${selected.sentTo.join(', ')}` : ''}. Ta treść się nie zmieni.{' '}
                  <button
                    type="button"
                    onClick={() => setSelectedNo(offer.currentVersion)}
                    className="font-medium text-accent hover:underline"
                  >
                    Pokaż bieżącą
                  </button>
                </ContextBanner>
              )}
              {!viewingOld && selected.state === 'sent' && selected.sentAt && (
                <p className="text-caption text-fg-muted">
                  Wysłana {formatDate(selected.sentAt)}
                  {selected.sentTo?.length ? ` do ${selected.sentTo.join(', ')}` : ''}.
                  {p.newVersion ? ' Zmiany tylko w nowej wersji.' : ''}
                </p>
              )}
              {compare && previous ? (
                <VersionCompare before={previous} after={selected} seesRates={seesRates} />
              ) : (
                <OfferContentView
                  version={selected}
                  emptyAction={
                    p.edit && !viewingOld ? (
                      <Button variant="secondary" icon={Pencil} onClick={() => setEditing(true)}>
                        Uzupełnij ofertę
                      </Button>
                    ) : undefined
                  }
                />
              )}
            </div>
          ) : null}
        </Section>

        <aside className="order-1 flex flex-col gap-4 lg:order-2" aria-label="Podsumowanie i wersje">
          <Summary
            offer={offer}
            version={current}
            seesRates={seesRates}
            editing={editing}
            onSend={() => setDialog('send')}
            onSubmit={() =>
              void run(
                () => submit.mutateAsync({ id: offer.id, version: offer.version }),
                'Oferta czeka na akceptację właściciela',
              )
            }
            submitting={submit.isPending}
          />
          <Versions
            versions={offer.versions}
            selectedNo={selected?.versionNo ?? offer.currentVersion}
            onSelect={(n) => {
              setSelectedNo(n);
              setCompare(false);
            }}
            canCompare={Boolean(previous) && !editing}
            compare={compare}
            onToggleCompare={() => setCompare((c) => !c)}
            previousNo={previous?.versionNo ?? null}
          />
        </aside>
      </div>

      <SendOfferDialog offer={offer} open={dialog === 'send'} onOpenChange={(o) => !o && setDialog(null)} />
      <OutcomeDialog
        offer={offer}
        outcome={dialog === 'rejected' ? 'rejected' : 'accepted'}
        open={dialog === 'accepted' || dialog === 'rejected'}
        onOpenChange={(o) => !o && setDialog(null)}
      />
    </Page>
  );
}

/**
 * Banery oferty. Stan akceptacji wysyłki (kompozycja Fali 3): czeka w Akceptacjach → zatwierdzona, wysyłana → wysłana
 * (z oznaczeniem symulacji) albo błąd wysyłki; prośba nieaktualna po zmianie treści.
 */
function Banners({ offer }: { offer: OfferDetail }) {
  const access = useAccess();
  const pending = offer.pendingSend;
  const send = offer.sendApproval;
  const sendLink = send && access.can('approvals') ? approvalCardPath(send.id) : undefined;
  const banners: ReactNode[] = [];
  if (pending && !pending.stale && pending.status === 'pending') {
    banners.push(
      <ContextBanner
        key="send"
        tone="accent"
        icon={ShieldCheck}
        linkTo={access.can('approvals') ? approvalCardPath(pending.approvalId) : undefined}
      >
        Wysyłka wersji {pending.versionNo} czeka w Akceptacjach. Nic nie wyjdzie bez zgody.
      </ContextBanner>,
    );
  }
  if (send && isApprovalInFlight(send)) {
    banners.push(
      <ContextBanner key="sending" tone="accent" icon={ShieldCheck} linkTo={sendLink}>
        Wysyłka wersji {send.versionNo} zatwierdzona. Wysyłamy…
      </ContextBanner>,
    );
  }
  if (send?.status === 'succeeded' && send.simulated && offer.status === 'sent') {
    banners.push(
      <ContextBanner key="simulated" tone="info" linkTo={sendLink} linkLabel="Akceptacja">
        Wersja {send.versionNo} oznaczona jako wysłana. {APPROVAL_SIMULATION_NOTE}.
      </ContextBanner>,
    );
  }
  if (send?.status === 'failed') {
    banners.push(
      <ContextBanner key="failed" tone="warning" linkTo={sendLink} linkLabel="Akceptacja">
        Wysyłka wersji {send.versionNo} nie powiodła się{send.error ? `: ${send.error}` : '.'}
        {send.manualCheckRequired ? ` ${APPROVAL_MESSAGES.manualCheck}: sprawdź skrzynkę, zanim wyślesz ponownie.` : ''}
      </ContextBanner>,
    );
  }
  if (pending?.stale) {
    banners.push(
      <ContextBanner key="stale" tone="warning">
        Oferta zmieniła się po prośbie o wysyłkę. Ta prośba się nie wykona — wyślij ofertę ponownie.
      </ContextBanner>,
    );
  }
  if (offer.status === 'pending_owner') {
    banners.push(
      <ContextBanner key="owner" tone="accent" icon={UserCheck}>
        {access.isAdmin
          ? 'Oferta czeka na Twoją akceptację. Sprawdź stawkę i warunki, potem wyślij albo cofnij do poprawy.'
          : 'Oferta czeka na akceptację właściciela.'}
      </ContextBanner>,
    );
  }
  if (banners.length === 0) return null;
  return <div className="mt-[18px] flex flex-col gap-2">{banners}</div>;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-fg-muted">{label}</dt>
      <dd className="min-w-0 text-right text-fg">{children}</dd>
    </div>
  );
}

function Summary({
  offer,
  version,
  seesRates,
  editing,
  onSend,
  onSubmit,
  submitting,
}: {
  offer: OfferDetail;
  version: OfferVersionDto | undefined;
  seesRates: boolean;
  editing: boolean;
  onSend: () => void;
  onSubmit: () => void;
  submitting: boolean;
}) {
  const c = version?.content;
  const p = offer.permissions;
  const total = seesRates && version?.totalNet ? offerMoneyText(version.totalNet, offer.currency) : null;
  let action: ReactNode = null;
  if (!editing && p.requestSend) {
    action = (
      <>
        <Button variant="primary" icon={Send} fullWidth onClick={onSend} className="max-md:h-11">
          Wyślij do klienta
        </Button>
        <p className="text-caption text-fg-muted">
          Trafi do Akceptacji. Po zatwierdzeniu wyślemy ofertę mailem, a ta wersja zostanie zamrożona.
        </p>
      </>
    );
  } else if (!editing && p.submitToOwner) {
    action = (
      <>
        <Button
          variant="primary"
          icon={UserCheck}
          fullWidth
          loading={submitting}
          onClick={onSubmit}
          className="max-md:h-11"
        >
          Przekaż właścicielowi
        </Button>
        <p className="text-caption text-fg-muted">
          Właściciel sprawdzi stawkę i warunki, potem wyśle ofertę klientowi.
        </p>
      </>
    );
  } else if (offer.status === 'sent' && offer.lastSentAt) {
    action = (
      <p className="text-caption text-fg-muted">
        Wysłana {formatDateShort(offer.lastSentAt)}. Gdy klient odpowie, oznacz ofertę jako przyjętą albo odrzuconą.
      </p>
    );
  } else if (offer.status === 'accepted') {
    action = <p className="text-caption text-fg-muted">To główna oferta klienta: godziny widać w jego profilu.</p>;
  }

  return (
    <section aria-label="Podsumowanie" className="rounded-xl border border-line bg-panel p-4 md:p-6">
      <h2 className="text-body-s font-semibold text-fg">Podsumowanie</h2>
      <dl className="mt-3 flex flex-col gap-2.5 text-body-s">
        <Row label={c?.package ? 'Pakiet' : 'Czas'}>
          <Mono>{c?.estimatedSeconds ? formatDuration(c.estimatedSeconds) : '—'}</Mono>
        </Row>
        {seesRates && (
          <Row label="Stawka">
            <Mono>{c?.rate ? offerRateText(c.rate, c.currency) : '—'}</Mono>
          </Row>
        )}
        {c?.timeline && <Row label="Termin">{c.timeline}</Row>}
      </dl>
      {seesRates && (
        <div className="mt-4 flex items-baseline justify-between gap-3 border-t border-line pt-4">
          <span className="text-body-s font-semibold text-fg">Razem netto</span>
          <Mono className="text-h4 text-fg">{total ?? '—'}</Mono>
        </div>
      )}
      {action && <div className="mt-4 flex flex-col gap-2">{action}</div>}
    </section>
  );
}

function Versions({
  versions,
  selectedNo,
  onSelect,
  canCompare,
  compare,
  onToggleCompare,
  previousNo,
}: {
  versions: OfferVersionDto[];
  selectedNo: number;
  onSelect: (versionNo: number) => void;
  canCompare: boolean;
  compare: boolean;
  onToggleCompare: () => void;
  previousNo: number | null;
}) {
  const ordered = [...versions].reverse();
  return (
    <section aria-label="Wersje" className="rounded-xl border border-line bg-panel p-2">
      <h2 className="px-3 pt-2 pb-1 text-caption font-medium text-fg-muted">Wersje</h2>
      <ul className="flex flex-col">
        {ordered.map((v) => (
          <li key={v.id}>
            <button
              type="button"
              aria-pressed={v.versionNo === selectedNo}
              onClick={() => onSelect(v.versionNo)}
              className={cn(
                'flex min-h-10 w-full flex-col justify-center rounded-lg px-3 py-1.5 text-left transition-colors',
                'hover:bg-surface focus-visible:outline-2 focus-visible:outline-accent',
                v.versionNo === selectedNo && 'bg-elevated hover:bg-elevated',
              )}
            >
              <span className="text-body-s font-medium text-fg">{versionText(v)}</span>
              {v.author && <span className="text-caption text-fg-muted">{v.author.name}</span>}
            </button>
          </li>
        ))}
      </ul>
      {canCompare && previousNo !== null && (
        <div className="px-1 pt-1">
          <Button variant="ghost" size="s" className={TOUCH_BUTTON} onClick={onToggleCompare}>
            {compare ? 'Pokaż treść' : `Porównaj z wersją ${previousNo}`}
          </Button>
        </div>
      )}
    </section>
  );
}
